import type {
  SandboxDeployedRecord,
  SandboxDesiredManifest,
  SandboxPythonPackage,
} from '@/versions/v1/shared/admin-seed';

export const EXACT_VERSION_PATTERN =
  /^[0-9]+(\.[0-9]+)*((a|b|rc)[0-9]+)?(\.post[0-9]+)?(\.dev[0-9]+)?$/;

export const PYTHON_PACKAGE_NAME_PATTERN =
  /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/;

export const PYTHON_VERSION_PATTERN =
  /^[a-zA-Z0-9*+!~<>=.,_\[\]\s-]+$/;

export const PRESET_LABEL = 'Add document toolchain (docx · xlsx · pptx)';

export const DOCUMENT_TOOLCHAIN_PRESET: readonly SandboxPythonPackage[] = [
  { name: 'python-docx', version: '1.2.0' },
  { name: 'openpyxl', version: '3.1.5' },
  { name: 'python-pptx', version: '1.0.2' },
  { name: 'XlsxWriter', version: '3.2.9' },
] as const;

export const EMPTY_SANDBOX_DESIRED_MANIFEST: SandboxDesiredManifest = {
  python: [],
  apt: [],
  notes: '',
};

export type PypiVersionCheck = {
  requested: string;
  status: 'exists' | 'missing' | 'yanked' | 'unchecked';
  yankedReason: string | null;
};

export type PypiVerifyResult =
  | {
      status: 'found';
      requestedName: string;
      canonicalName: string;
      latestVersion: string;
      summary: string | null;
      version: PypiVersionCheck | null;
      checkedAt: number;
    }
  | { status: 'not_found'; requestedName: string; checkedAt: number }
  | { status: 'unreachable'; requestedName: string; checkedAt: number };

export type RowVerifyState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'done'; result: PypiVerifyResult };

export function normalizePypiName(name: string): string {
  return name.toLowerCase().replace(/[-_.]+/g, '-');
}

export function isValidPythonVersion(version: string): boolean {
  return PYTHON_VERSION_PATTERN.test(version) && /[0-9a-zA-Z]/.test(version);
}

export function buildDockerfileSnippet(desired: SandboxDesiredManifest): string {
  const pipArgs =
    desired.python.length === 0
      ? ''
      : desired.python.map((pkg) => `${pkg.name}==${pkg.version}`).join(' ');
  const pipLine =
    desired.python.length === 0
      ? '# RUN pip install --no-cache-dir <packages>'
      : `RUN pip install --no-cache-dir ${pipArgs}`;
  return ['FROM daytonaio/sandbox:0.5.0-slim', 'USER root', pipLine].join('\n');
}

export function isManifestDirty(
  initial: SandboxDesiredManifest,
  draft: SandboxDesiredManifest,
): boolean {
  return JSON.stringify(initial) !== JSON.stringify(draft);
}

export function cleanManifest(draft: SandboxDesiredManifest): SandboxDesiredManifest {
  return {
    python: draft.python.filter(
      (pkg) => pkg.name.trim() !== '' && pkg.version.trim() !== '',
    ),
    apt: draft.apt.filter((pkg) => pkg.trim() !== ''),
    notes: draft.notes,
  };
}

export function applyDocumentToolchainPreset(
  draft: SandboxDesiredManifest,
): SandboxDesiredManifest {
  const existingKeys = new Set(draft.python.map((pkg) => normalizePypiName(pkg.name)));
  const toAppend = DOCUMENT_TOOLCHAIN_PRESET.filter(
    (pkg) => !existingKeys.has(normalizePypiName(pkg.name)),
  );
  return {
    ...draft,
    python: [...draft.python, ...toAppend.map((pkg) => ({ ...pkg }))],
  };
}

export function rowKey(pkg: SandboxPythonPackage): string {
  return `${pkg.name}@${pkg.version}`;
}

export function mockPypiVerify(
  pkgName: string,
  version: string,
  anchorMs: number,
): PypiVerifyResult {
  if (pkgName.toLowerCase().includes('missing')) {
    return { status: 'not_found', requestedName: pkgName, checkedAt: anchorMs };
  }
  return {
    status: 'found',
    requestedName: pkgName,
    canonicalName: normalizePypiName(pkgName),
    latestVersion: version.trim() || '1.0.0',
    summary: 'Mock PyPI summary for surface-only verification.',
    version:
      version.trim() && EXACT_VERSION_PATTERN.test(version.trim())
        ? { requested: version.trim(), status: 'exists', yankedReason: null }
        : null,
    checkedAt: anchorMs,
  };
}

export function formatDeployedDate(
  builtAt: number | undefined,
  formatter: (ms: number) => string,
): string | null {
  return builtAt ? formatter(builtAt) : null;
}

export function statusLineMode(
  deployed: SandboxDeployedRecord | null,
  drift: boolean,
): 'live' | 'drift' | 'not-live' {
  if (deployed && !drift) return 'live';
  if (deployed && drift) return 'drift';
  return 'not-live';
}
